/**
 * @file 消耗总览
 * @author gaoxunchen
 */

import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@tencent/spaui';
import Icon from '@tencent/od-icon';
import { useHistory } from 'react-router-dom';
import { ACCOUNT_TYPE, REACT_APP_PUBLIC_URL } from '@/constants/Constants';
import globalStore from '@/stores';
import { User } from '@/model/user';
import { ROLES_NAME_TO_ID } from '@/pages/agency/permission-config';
import { observer } from 'mobx-react';
import { jump_edu } from '@/api/operator/cgi/smb/manage/role';

import './fast-entry.scoped.less';

interface FastEntryItem {
  label: string;
  href: string;
  id?: string;
  perm?: (loginUser: User) => boolean;
  purview?: boolean;
  enableKey?: string;
  target?: '_self' | '_new'; // 默认为_self
  endIcon?: string | boolean; // 为boolean且=true时，使用默认图标，为string时，可传自定义图标
}

export interface FastEntryProps {
  data: FastEntryItem[][];
}

// perm是函数，无法加入到配置中
const permIdMap = {
  'queryCustomer': (loginStatus: Record<string, any>) => loginStatus.role_list[ROLES_NAME_TO_ID.AGENCYOPERATORROLE_AGENCY_BOSS] || loginStatus.role_list[ROLES_NAME_TO_ID.AGENCYOPERATORROLE_AGENCY_ADMIN],
  'bm': (loginStatus: Record<string, any>) => loginStatus.account_type !== ACCOUNT_TYPE.direct,
  'dmp': (loginStatus: Record<string, any>) => loginStatus.account_type !== ACCOUNT_TYPE.direct,
};

/**
 * 常用入口
 */
const FastEntry: React.FC<FastEntryProps> = (props) => {
  const { data } = props;
  const account = globalStore.account || {};
  const { agency_uid } = account;

  const [finalItems, setFinalItems] = useState<FastEntryItem[][]>([]);

  const history = useHistory();

  const resetDataItems = useCallback(({
    eduUrl,
  }: {
    eduUrl?: string;
  }) => {
    const _finalItems = data.map((dataArr) => {
      return dataArr.filter((item) => {
        if (eduUrl && item.id === 'edu') {
          item.href = eduUrl;
        }
        const perm = permIdMap[item.id ?? ''];
        // 销售无权限
        if (perm && !perm(account)) {
          return false;
        }

        // 运营开关未开启，无权限
        if (item.enableKey && account.feature_flag[item.enableKey] !== 1) {
          return false;
        }

        // 运营无权限
        if (!_.isNil(item.purview) && account.privilege.indexOf(Number(item.purview)) === -1) {
          return false;
        }
        return true;
      });
    });
    setFinalItems(_finalItems);
  }, [data, account]);

  const handleClickItem = useCallback((item: FastEntryItem) => {
    if (item?.href) {
      const target = item.target || '_self';
      if (/https?\:\/\//.test(item.href)) {
        window.open(item.href, '_new');
      } else if ('_self' == target) {
        history.push(item.href);
      } else {
        window.open(`${REACT_APP_PUBLIC_URL}${item.href}`, target);
      }
    }
  }, []);

  // 营销学院的跳转链接初始化
  const initEdu = useCallback(async () => {
    const eduUrl = await jump_edu({
      data: {
        agency_uid,
        user_id: globalStore.currentUserId,
      },
    }).then((res: any) => {
      return res?.data?.crm_tool_box_url;
    }, () => null);
    resetDataItems({ eduUrl });
  }, [agency_uid]);

  useEffect(() => {
    initEdu();
    resetDataItems({ });
  }, [agency_uid]);

  return (
    <div className='fast-entry'>
    {
      finalItems.map((row, index) => {
        return <div className='row' key={index}>
          {
            row.map((item, index) => {
              const icon = typeof item.endIcon == 'boolean'
                ? item.endIcon ? <Icon name='share-outlined'/> : null
                : typeof item.endIcon == 'string'
                  ? <Icon name={item.endIcon as any}/>
                  : null;
              return <span className='item' key={index}>
                <Button onClick={() => handleClickItem(item)} endIcon={icon}>{item.label}</Button>
              </span>;
            })
          }
        </div>;
      })
    }
    </div>
  );
};

export default observer(FastEntry);
